import "./src/styles/global.css";
import "./src/styles/workshop-codex.css";
import { initLandingPostHog } from "./src/posthog";
import { captureLandingAttribution } from "./src/attribution";
import { installStaleDeployRecovery } from "./src/staleDeployRecovery";
import { installStartGoalLinks } from "./src/startGoalLinks";
import { adoptServedHomeArm } from "./src/homeArm";
import { syncThemeForLocation } from "./src/homeTheme";
import { mountThemeSwitch } from "./src/components/ThemeSwitch";
import {
  mountAndroidAppBanner,
  syncAndroidAppBannerForLocation,
} from "./src/components/AndroidAppBanner";
import type { GatsbyBrowser } from "gatsby";
import { wrapWithPageReviews } from "./src/reviews/ReviewsContext";

// PostHog's `2026-01-30` defaults set `capture_pageview: "history_change"`,
// which fires an initial `$pageview` on init *and* on every pushState /
// popstate Gatsby emits when the user navigates between pages. So we just
// need to bring the SDK up once on first client entry — page tracking,
// session recording opt-ins, and autocapture are all handled by the
// defaults bundle.
//
// `captureLandingAttribution` runs FIRST so the `.eden.so`-scoped
// `eden_attribution` cookie exists before any analytics events fire.
// First-touch only — re-visits don't overwrite the original campaign.
// See `src/attribution.ts` and `marketing/utm-conventions.md`.
export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  // Before Gatsby compares page paths: keep the paths homepage on "/"
  // instead of letting Gatsby switch the address to /home-paths/ (homeArm.ts).
  adoptServedHomeArm();
  // Install first: recover the visitor from a stale-deploy chunk error
  // (old tab + new build → missing chunk → "Unexpected token '<'" /
  // ChunkLoadError) with a guarded one-shot reload, before anything else runs.
  installStaleDeployRecovery();
  captureLandingAttribution();
  initLandingPostHog();
  // Feature pages send people into the app with their goal (first-win plan lane 10).
  installStartGoalLinks(process.env.GATSBY_APP_SIGN_IN_URL || "https://app.eden.so");
};

// Light mode follows the page on client-side navigation (src/homeTheme.ts):
// the head script only runs on a full load.
export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = () => {
  syncThemeForLocation();
  // The Android banner stays off the homepage (its buttons go to Play already).
  syncAndroidAppBannerForLocation();
};

// The bottom-right light/dark switch, in its own root after the page is up
// (components/ThemeSwitch.tsx).
export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] = () => {
  mountThemeSwitch();
  // "Get the app" bar on Android phones; iPhone gets Safari's own banner.
  // Neither shows on the homepage (appStoreLinks.ts isHomepagePath).
  mountAndroidAppBanner();
};

// Same provider as gatsby-ssr, so hydration matches the static HTML.
export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({ element, props }) =>
  wrapWithPageReviews(element, props.pageContext);
